import { cn } from "@/lib/cn"
import { cssEase, GAME_DURATION, GAME_EASE, toMs } from "@/lib/motion"
import { useEffect, useRef } from "react"
import { confettiBurst } from "./prize-wheel-geometry"
import { WHEEL_PALETTE } from "./prize-wheel-palette"

const CONFETTI_COUNT = 32
const COLORS = WHEEL_PALETTE.confetti

/** A short confetti burst from the hub in gold, ivory and ink, clipped to the box of the wheel so nothing shifts. */
export function WheelConfetti() {
  const layerRef = useRef<HTMLDivElement>(null)
  const pieces = confettiBurst(CONFETTI_COUNT, COLORS.length)

  useEffect(() => {
    const layer = layerRef.current
    if (!layer) return
    const side = layer.clientWidth
    const burst = confettiBurst(CONFETTI_COUNT, COLORS.length)
    const animations = Array.from(layer.children).map((node, index) => {
      const piece = burst[index]
      if (!piece) return null
      const x = piece.x * side
      const y = piece.y * side
      return node.animate(
        [
          { transform: "translate(0px, 0px) rotate(0deg) scale(0.4)", opacity: 0 },
          { transform: `translate(${x * 0.4}px, ${y * 0.4}px) rotate(${piece.turn * 0.3}deg) scale(1)`, opacity: 1, offset: 0.12 },
          { transform: `translate(${x}px, ${y}px) rotate(${piece.turn}deg) scale(1)`, opacity: 1, offset: 0.55 },
          { transform: `translate(${x}px, ${y + piece.fall * side}px) rotate(${piece.turn * 1.4}deg) scale(0.9)`, opacity: 0 },
        ],
        { duration: toMs(GAME_DURATION.confetti), delay: piece.delay, easing: cssEase(GAME_EASE.confetti), fill: "both" },
      )
    })
    return () => animations.forEach((animation) => animation?.cancel())
  }, [])

  return (
    <div ref={layerRef} aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((piece, index) => (
        <span
          key={`confetti-${index}`}
          className={cn("absolute left-1/2 top-1/2 opacity-0", piece.round
              ? "-ml-game-confetti-offset -mt-game-confetti-offset size-game-confetti rounded-full"
              : "-ml-game-strip-offset-x -mt-game-strip-offset-y h-game-strip w-game-strip rounded-game-strip")}
          style={{ backgroundColor: COLORS[piece.color] }}
        />
      ))}
    </div>
  )
}
